import * as React from 'react';

import {
    Box,
    Button,
    Divider,
    FormControlLabel,
    Menu,
    MenuItem,
    RadioGroup,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    createInvoiceIcon,
    createQuoteIcon,
    FaOptionIcon,
    todoIcon,
} from '@europrocurement/l2d-icons';
import { FlexyButton, PriceLigne, Radio, VerticalDivider } from '@europrocurement/flexy-components';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import {
    CollectionDeDossierJsonldCollectiondossierRead,
    ReglementsEtAcomptesCBJsonldAcompteRead,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { DossiersArticlesReductions } from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierPrefacturation';
import { FactureJsonldFactureReadTimestampableBlameambleGroup } from '@europrocurement/l2d-domain/openApi/ApiFacturation';
import { Container, SxProps, useMediaQuery } from '@mui/system';
import { faEllipsis } from '@fortawesome/pro-duotone-svg-icons';
import { facturationType, heightFooterFacturation } from '../../functions';

type TotalsSectionProps = {
    collectionsId: number[];
    totalAcomptes: number;
    acomptes: ReglementsEtAcomptesCBJsonldAcompteRead[];
    typeFacturation: facturationType;
    typesFacturation: {
        label: string;
        value: string;
        description: string;
    }[];
    dataPrefacturation: DossiersArticlesReductions;
    ajustInvoice: () => Promise<void>;
    afterSubmitInvoice: () => void;
    reloadInvoiceEstimates: () => void;
    updateFacturationType: (newTypeFacturation: facturationType) => Promise<void>;
    createEstimates: (
        idCollections: number[],
    ) => Promise<CollectionDeDossierJsonldCollectiondossierRead>;
    createInvoice: (
        prefacture: DossiersArticlesReductions,
        acompteIds: Array<string>,
    ) => Promise<FactureJsonldFactureReadTimestampableBlameambleGroup>;
    createProforma: (
        prefacture: DossiersArticlesReductions,
        acompteIds: Array<string>,
    ) => Promise<FactureJsonldFactureReadTimestampableBlameambleGroup>;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setTypeFacturation: React.Dispatch<React.SetStateAction<facturationType>>;
};

const TotalsSection: React.FunctionComponent<TotalsSectionProps> = function ({
    collectionsId,
    totalAcomptes,
    acomptes,
    typeFacturation,
    typesFacturation,
    dataPrefacturation,
    ajustInvoice,
    afterSubmitInvoice,
    reloadInvoiceEstimates,
    updateFacturationType,
    createEstimates,
    createInvoice,
    createProforma,
    setLoading,
    setTypeFacturation,
}: TotalsSectionProps) {
    const { enqueueSnackbar } = useSnackbar();
    const { palette } = useTheme();
    const PriceLigneSx: SxProps = { gap: '15px' };
    const isLg = useMediaQuery('(min-width:1024px)');
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClickContextualMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const proformaTotalSx = {
        width: '100%',
        fontSize: '13px',
        fontWeight: '500',
        lineHeight: '15.6px',
        color: palette.grey[500],
    };
    return (
        <>
            <Box
                sx={{
                    background: palette.background.paper,
                    position: 'fixed',
                    boxShadow: '1px 1px 15px 0px rgba(0, 0, 0, 0.2)',
                    bottom: '0px',
                    left: '0px',
                    width: '100%',
                    height: heightFooterFacturation,
                }}
            />
            <Container
                sx={{
                    position: 'fixed',
                    bottom: '0px',
                    display: 'flex',
                    flexDirection: {
                        xs: 'column',
                        sm: 'column',
                        md: 'row',
                        lg: 'row',
                        xl: 'row',
                    },
                    height: heightFooterFacturation,
                    justifyContent: 'space-around',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '15px',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Box>
                        <PriceLigne
                            label="Reste à facturer : "
                            sx={PriceLigneSx}
                            price={`${dataPrefacturation.resteAFacturer} €`}
                        />
                        {dataPrefacturation.ttcProforma &&
                        Number(dataPrefacturation.ttcProforma) > 0 ? (
                            <Typography
                                sx={proformaTotalSx}
                            >{`Dont ${dataPrefacturation.ttcProforma} € en proforma`}</Typography>
                        ) : null}
                    </Box>
                    <Box>
                        <PriceLigne
                            labelTypographyProps={{ fontWeight: '600' }}
                            priceTypographyProps={{ fontWeight: '600' }}
                            label="Provision non affectée : "
                            sx={PriceLigneSx}
                            price={`${totalAcomptes} €`}
                        />
                        <Divider />
                        <PriceLigne
                            labelTypographyProps={{ fontWeight: '600' }}
                            priceTypographyProps={{ fontSize: '22px', fontWeight: '600' }}
                            label="Facturable : "
                            sx={PriceLigneSx}
                            price={`${dataPrefacturation.ttc} €`}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        flexDirection: {
                            xs: 'row',
                            sm: 'row',
                            md: 'column',
                            lg: 'column',
                            xl: 'column',
                        },
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                    >
                        {typeFacturation && (
                            <RadioGroup
                                sx={{ flexGrow: '1' }}
                                aria-labelledby="invoice-type-radio-buttons-group-label"
                                color={palette.primary.dark}
                                name="invoice-type-buttons-group"
                                value={typeFacturation}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    const newValue = event.target.value;
                                    updateFacturationType(newValue as facturationType);
                                    setTypeFacturation(newValue as facturationType);
                                }}
                            >
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    {typesFacturation.map(({ value, label, description }) => (
                                        <Tooltip title={description}>
                                            <FormControlLabel
                                                value={value}
                                                control={<Radio />}
                                                label={label}
                                            />
                                        </Tooltip>
                                    ))}
                                </Box>
                            </RadioGroup>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'row',
                            gap: '5px',
                        }}
                    >
                        <FlexyButton
                            variant="text"
                            color={palette.primary.dark}
                            disabled={
                                !(
                                    Math.abs(
                                        parseFloat(
                                            dataPrefacturation.ttc ? dataPrefacturation.ttc : '0',
                                        ) - totalAcomptes,
                                    ) <= 15
                                ) || !dataPrefacturation.facturable
                            }
                            onClick={() => {
                                setLoading(true);
                                ajustInvoice()
                                    .catch((error) => {
                                        setLoading(false);
                                        console.error('Une erreur est survenue:', error);
                                        if (
                                            error instanceof AxiosError &&
                                            error.response &&
                                            error.response.data.exception &&
                                            error.response.data.exception.message
                                        ) {
                                            enqueueSnackbar(
                                                <Typography>
                                                    {error.response.data.exception.message}
                                                </Typography>,
                                                {
                                                    variant: 'error',
                                                },
                                            );
                                        } else if (error instanceof Error) {
                                            enqueueSnackbar(
                                                <Typography>{error.message}</Typography>,
                                                {
                                                    variant: 'error',
                                                },
                                            );
                                        }
                                    })
                                    .then(() => {
                                        setLoading(false);
                                    });
                            }}
                            style={{ float: 'right', fontWeight: '500' }}
                            icon={todoIcon}
                            label={isLg ? 'Ajuster le prix' : 'Ajuster'}
                        />
                        <Tooltip
                            title={
                                !dataPrefacturation.facturable
                                    ? dataPrefacturation.motifNonFacturable
                                    : null
                            }
                        >
                            <Box>
                                <FlexyButton
                                    disabled={!dataPrefacturation.facturable}
                                    variant="contained"
                                    color={palette.primary.dark}
                                    onClick={() => {
                                        setLoading(true);
                                        const acompteIds: string[] = acomptes.flatMap((acompte) =>
                                            (acompte.detailsReglements ?? []).map((detail) => {
                                                if (detail.id === undefined || detail.id === null) {
                                                    throw new Error('detail sans id');
                                                }
                                                return detail.id as unknown as string;
                                            }),
                                        );
                                        createInvoice(dataPrefacturation, acompteIds)
                                            .catch((error) => {
                                                setLoading(false);
                                                console.error('Une erreur est survenue:', error);
                                                if (
                                                    error instanceof AxiosError &&
                                                    error.response &&
                                                    error.response.data.exception &&
                                                    error.response.data.exception.message
                                                ) {
                                                    enqueueSnackbar(
                                                        <Typography>
                                                            {error.response.data.exception.message}
                                                        </Typography>,
                                                        {
                                                            variant: 'error',
                                                        },
                                                    );
                                                } else if (error instanceof Error) {
                                                    enqueueSnackbar(
                                                        <Typography>{error.message}</Typography>,
                                                        {
                                                            variant: 'error',
                                                        },
                                                    );
                                                }
                                            })
                                            .then(() => {
                                                afterSubmitInvoice();
                                                // Add notification to user.
                                                setLoading(false);
                                                enqueueSnackbar('La facture a été créée.', {
                                                    variant: 'success',
                                                });
                                                reloadInvoiceEstimates();

                                                const devisEtFacturesFormElement =
                                                    document.getElementById('devisEtFactures');
                                                if (devisEtFacturesFormElement) {
                                                    devisEtFacturesFormElement.scrollIntoView();
                                                }
                                            });
                                    }}
                                    icon={createInvoiceIcon}
                                    label={isLg ? 'Créer la facture' : 'Facture'}
                                />
                            </Box>
                        </Tooltip>

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <VerticalDivider sx={{ margin: '0px 10px' }} />

                            <Button
                                id="toolbar-contextual-menu-btn"
                                aria-controls={open ? 'toolbar-contextual-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                                onClick={handleClickContextualMenu}
                                sx={{ color: 'text.gris70' }}
                            >
                                <Box>
                                    <FaOptionIcon
                                        icon={faEllipsis}
                                        size="lg"
                                    />
                                </Box>
                            </Button>
                            <Menu
                                id="toolbar-contextual-menu"
                                aria-labelledby="toolbar-contextual-menu"
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                                MenuListProps={{ sx: { py: 0 } }}
                            >
                                <MenuItem
                                    onClick={() => {
                                        setLoading(true);
                                        createEstimates(collectionsId)
                                            .then(() => {
                                                // Add notification to user.
                                                enqueueSnackbar('Le devis a été créé.', {
                                                    variant: 'success',
                                                });
                                                setLoading(false);
                                                reloadInvoiceEstimates();

                                                const devisEtFacturesFormElement =
                                                    document.getElementById('devisEtFactures');
                                                if (devisEtFacturesFormElement) {
                                                    devisEtFacturesFormElement.scrollIntoView();
                                                }
                                            })
                                            .catch((error) => {
                                                setLoading(false);
                                                console.error('Une erreur est survenue:', error);
                                                if (
                                                    error instanceof AxiosError &&
                                                    error.response &&
                                                    error.response.data.exception &&
                                                    error.response.data.exception.message
                                                ) {
                                                    enqueueSnackbar(
                                                        <Typography>
                                                            {error.response.data.exception.message}
                                                        </Typography>,
                                                        {
                                                            variant: 'error',
                                                        },
                                                    );
                                                } else if (error instanceof Error) {
                                                    enqueueSnackbar(
                                                        <Typography>{error.message}</Typography>,
                                                        {
                                                            variant: 'error',
                                                        },
                                                    );
                                                }
                                            });
                                    }}
                                    sx={{ gap: '4px' }}
                                >
                                    <FaOptionIcon
                                        {...createQuoteIcon.props}
                                        color="text.gris70"
                                    />
                                    <Typography sx={{ color: 'text.primary' }}>
                                        Créer le devis
                                    </Typography>
                                </MenuItem>
                                {dataPrefacturation.facturable ? (
                                    <MenuItem
                                        onClick={() => {
                                            setLoading(true);
                                            const acompteIds: string[] = acomptes.flatMap(
                                                (acompte) =>
                                                    (acompte.detailsReglements ?? []).map(
                                                        (detail) => {
                                                            if (
                                                                detail.id === undefined ||
                                                                detail.id === null
                                                            ) {
                                                                throw new Error('detail sans id');
                                                            }
                                                            return detail.id as unknown as string;
                                                        },
                                                    ),
                                            );
                                            createProforma(dataPrefacturation, acompteIds)
                                                .catch((error) => {
                                                    setLoading(false);
                                                    console.error(
                                                        'Une erreur est survenue:',
                                                        error,
                                                    );
                                                    if (
                                                        error instanceof AxiosError &&
                                                        error.response &&
                                                        error.response.data.exception &&
                                                        error.response.data.exception.message
                                                    ) {
                                                        enqueueSnackbar(
                                                            <Typography>
                                                                {
                                                                    error.response.data.exception
                                                                        .message
                                                                }
                                                            </Typography>,
                                                            {
                                                                variant: 'error',
                                                            },
                                                        );
                                                    } else if (error instanceof Error) {
                                                        enqueueSnackbar(
                                                            <Typography>
                                                                {error.message}
                                                            </Typography>,
                                                            {
                                                                variant: 'error',
                                                            },
                                                        );
                                                    }
                                                })
                                                .then(() => {
                                                    afterSubmitInvoice();
                                                    // Add notification to user.
                                                    setLoading(false);
                                                    enqueueSnackbar('Le proforma a été créé.', {
                                                        variant: 'success',
                                                    });
                                                    reloadInvoiceEstimates();

                                                    const devisEtFacturesFormElement =
                                                        document.getElementById('devisEtFactures');
                                                    if (devisEtFacturesFormElement) {
                                                        devisEtFacturesFormElement.scrollIntoView();
                                                    }
                                                });
                                        }}
                                        sx={{ gap: '4px' }}
                                    >
                                        <FaOptionIcon
                                            {...createInvoiceIcon.props}
                                            color="text.gris70"
                                        />
                                        <Typography sx={{ color: 'text.primary' }}>
                                            Créer le proforma
                                        </Typography>
                                    </MenuItem>
                                ) : null}
                            </Menu>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </>
    );
};

export default TotalsSection;
